import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { HelpHubApiService, createDefaultApiService } from '../services/helpHubApiService';

// ============================================
// CONSTANTS
// ============================================
export const HELPHUB_WIDTH = 450;

// ============================================
// TYPES
// ============================================
export type HelpHubView =
  | 'home' | 'chat' | 'tickets' | 'ticket' | 'human';

export type HelpHubMode = 'authenticated' | 'guest';

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sources?: { title: string; url: string }[];
}

export interface TicketForm {
  category: string;
  subject: string;
  message: string;
  email: string;
}

export interface HelpHubUser {
  id?: string;
  name?: string;
  email?: string;
}

export interface Workspace {
  id: number;
  name: string;
  subscription_status: string;
  subscription_tier?: string;
  base_license_features?: Record<string, boolean>;
}

export interface SystemStatus {
  status: 'operational' | 'degraded' | 'down';
  uptime: string;
  lastChecked: string | Date;
}

export interface FrillToken {
  urls?: {
    ideas: string;
    roadmap: string;
    changelog: string;
  };
  token: string;
  expiresAt: string | Date;
}

export interface UserTicket {
  id: string;
  subject: string;
status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  updatedAt: Date;
}

export interface HelpHubConfig {
  mode: HelpHubMode;
  user: HelpHubUser;
  serverRegion: 'US' | 'EU';
  brand: {
    name: string;
    logo?: string;
    logoSmall?: string;
    primaryColor?: string;
  };
  features: {
    aiChat: boolean;
    liveChat: boolean;
    tickets: boolean;
    knowledgeBase: boolean;
    ideas: boolean;
    roadmap: boolean;
    changelog: boolean;
    videos: boolean;
    community: boolean;
    systemStatus: boolean;
  };
}

interface HelpHubContextType {
  isOpen: boolean;
  currentView: HelpHubView;
  messages: ChatMessage[];
  ticketForm: TicketForm;
  consentGiven: boolean;
  config: HelpHubConfig;
  isTyping: boolean;
  workspaces: Workspace[];
  workspacesLoading: boolean;
  systemStatus: SystemStatus | null;
  frillToken: FrillToken | null;
  userTickets: UserTicket[];
  ticketsLoading: boolean;
  unreadChangelog: number;
  pendingChatMessage: string | null;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setView: (view: HelpHubView) => void;
  goHome: () => void;
  addMessage: (text: string, isUser: boolean) => void;
  clearMessages: () => void;
  updateTicketForm: (updates: Partial<TicketForm>) => void;
  resetTicketForm: () => void;
  acceptConsent: () => void;
  setConfig: (config: Partial<HelpHubConfig>) => void;
  setIsTyping: (typing: boolean) => void;
  setPendingChatMessage: (text: string | null) => void;
  refreshSystemStatus: () => Promise<void>;
  refreshFrillToken: () => Promise<void>;
  refreshUserTickets: () => Promise<void>;
  markChangelogRead: () => void;
  hasHumanChatAccess: boolean;
  hasLiveChatFeature: boolean;
  activeWorkspaceCount: number;
  isGuest: boolean;
  userTier: string;
  apiService?: HelpHubApiService;
  onClose?: () => void;
}

// ============================================
// STORAGE KEYS
// ============================================
const STORAGE_KEYS = {
  consent: 'dfx_helphub_consent',
  messages: 'dfx_helphub_messages',
  ticketForm: 'dfx_helphub_ticket',
  changelogRead: 'dfx_helphub_changelog_read',
};

// ============================================
// DEFAULTS
// ============================================
const defaultConfig: HelpHubConfig = {
  mode: 'authenticated',
  user: {},
  serverRegion: 'US',
  brand: {
    name: 'DashboardFox',
  },
  features: {
    aiChat: true,
    liveChat: true,
    tickets: true,
    knowledgeBase: true,
    ideas: true,
    roadmap: true,
    changelog: true,
    videos: true,
    community: true,
    systemStatus: true,
  },
};

const defaultTicketForm: TicketForm = {
  category: '',
  subject: '',
  message: '',
  email: '',
};

// ============================================
// CONTEXT
// ============================================
const HelpHubContext = createContext<HelpHubContextType | null>(null);

// ============================================
// PROVIDER
// ============================================
interface HelpHubProviderProps {
  children: ReactNode;
  mode?: HelpHubMode;
  initialConfig?: Partial<HelpHubConfig>;
  // Dependencies - injected for flexibility
  authContext?: {
    user: any;
    serverRegion: string;
  };
  apiService?: HelpHubApiService;
  onClose?: () => void;
}

export const HelpHubProvider: React.FC<HelpHubProviderProps> = ({
  children,
  mode = 'authenticated',
  initialConfig,
  authContext,
  apiService,
  onClose: externalOnClose,
}) => {
  // Create stable apiService reference to prevent infinite loops
  const defaultApiServiceRef = useRef<HelpHubApiService | null>(null);
  if (!defaultApiServiceRef.current) {
    defaultApiServiceRef.current = createDefaultApiService();
  }
  const resolvedApiService = apiService || defaultApiServiceRef.current;

  // Core state
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState<HelpHubView>('home');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [ticketForm, setTicketForm] = useState<TicketForm>(defaultTicketForm);
  const [consentGiven, setConsentGiven] = useState(false);
  const [config, setConfigState] = useState<HelpHubConfig>({
    ...defaultConfig,
    mode,
    ...initialConfig,
  });
  const [isTyping, setIsTyping] = useState(false);
  const [pendingChatMessage, setPendingChatMessage] = useState<string | null>(null);

  // Data state
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(mode === 'authenticated');
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [frillToken, setFrillToken] = useState<FrillToken | null>(null);
  const [userTickets, setUserTickets] = useState<UserTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [unreadChangelog, setUnreadChangelog] = useState(0);

  // Ref to current ticket email — avoids stale closure in AuthContext sync effect
  // while preserving "only fill if empty" semantic without re-running on every email change.
  const ticketEmailRef = useRef(ticketForm.email);
  useEffect(() => {
    ticketEmailRef.current = ticketForm.email;
  }, [ticketForm.email]);

  // Sync mode when prop changes
  useEffect(() => {
    setConfigState(prev => ({ ...prev, mode }));
  }, [mode]);
  const isGuest = config.mode === 'guest';

  // Sync user from AuthContext (if provided) to config
  useEffect(() => {
    if (authContext?.user && !isGuest) {
      const user = authContext.user;
      setConfigState(prev => ({
        ...prev,
        user: {
          id: user.id?.toString(),
          name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
          email: user.email,
        },
        serverRegion: (authContext.serverRegion as 'US' | 'EU') || 'US',
      }));

      // Pre-fill ticket email — read via ref to avoid stale closure
      if (user.email && !ticketEmailRef.current) {
        setTicketForm(prev => ({ ...prev, email: user.email || '' }));
      }
    }
  }, [authContext?.user, authContext?.serverRegion, isGuest]);

  // Fetch workspaces for authenticated users
  useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!resolvedApiService.getWorkspaces || isGuest) {
        setWorkspacesLoading(false);
        return;
      }

      try {
        setWorkspacesLoading(true);
        const data = await resolvedApiService.getWorkspaces();
        setWorkspaces(data || []);
      } catch (error) {
        console.error('Failed to load workspaces for HelpHub:', error);
        setWorkspaces([]);
      } finally {
        setWorkspacesLoading(false);
      }
    };

    if (authContext?.user) {
      fetchWorkspaces();
    }
  }, [authContext?.user, isGuest, resolvedApiService]);

  // Fetch system status
  const refreshSystemStatus = useCallback(async () => {
    if (!resolvedApiService.getSystemStatus) return;

    try {
      const status = await resolvedApiService.getSystemStatus();
      setSystemStatus(status);
    } catch (error) {
      console.error('Failed to fetch system status:', error);
    }
  }, [resolvedApiService]);

  // Fetch Frill token for authenticated users
  const refreshFrillToken = useCallback(async () => {
    if (!resolvedApiService.getFrillToken || isGuest) return;

    try {
      const token = await resolvedApiService.getFrillToken();
      setFrillToken(token);
    } catch (error) {
      console.error('Failed to fetch Frill token:', error);
    }
  }, [isGuest, resolvedApiService]);

  // Fetch user tickets
  const refreshUserTickets = useCallback(async () => {
    if (isGuest) return;

    try {
      setTicketsLoading(true);
      const tickets = await resolvedApiService.getUserTickets();
      setUserTickets(tickets);
    } catch (error) {
      console.error('Failed to fetch user tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  }, [isGuest, resolvedApiService]);

  // Fetch unread changelog count
  useEffect(() => {
    if (isGuest) return;
    const fetchChangelogCount = async () => {
      if (!resolvedApiService.getUnreadChangelogCount) return;

      try {
        const count = await resolvedApiService.getUnreadChangelogCount();
        setUnreadChangelog(count);
      } catch (error) {
        console.error('Failed to fetch changelog count:', error);
      }
    };

    fetchChangelogCount();
  }, [isGuest, resolvedApiService]);

  // Initial data fetch
  useEffect(() => {
    refreshSystemStatus();
    if (!isGuest) {
      refreshFrillToken();
    }
  }, [refreshSystemStatus, refreshFrillToken, isGuest]);

  // Load saved state on mount
  useEffect(() => {
    try {
      const savedConsent = sessionStorage.getItem(STORAGE_KEYS.consent);
      if (savedConsent) setConsentGiven(JSON.parse(savedConsent));

      const savedMessages = sessionStorage.getItem(STORAGE_KEYS.messages);
      if (savedMessages) setMessages(JSON.parse(savedMessages));

      const savedTicket = sessionStorage.getItem(STORAGE_KEYS.ticketForm);
      if (savedTicket) setTicketForm(JSON.parse(savedTicket));
    } catch (e) {
      console.warn('Failed to load HelpHub state from sessionStorage:', e);
    }
  }, []);

  // Save state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.consent, JSON.stringify(consentGiven));
    } catch (e) {
      console.warn('Failed to save consent state:', e);
    }
  }, [consentGiven]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.messages, JSON.stringify(messages));
    } catch (e) {
      console.warn('Failed to save messages:', e);
    }
  }, [messages]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.ticketForm, JSON.stringify(ticketForm));
    } catch (e) {
      console.warn('Failed to save ticket form:', e);
    }
  }, [ticketForm]);

  // Actions
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    externalOnClose?.();
  }, [externalOnClose]);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const setView = useCallback((view: HelpHubView) => {
    // Load data when entering certain views
    if (view === 'tickets' && !isGuest) {
      refreshUserTickets();
    }
    setCurrentView(view);
  }, [refreshUserTickets, isGuest]);

  const goHome = useCallback(() => {
    setCurrentView('home');
  }, []);

  const addMessage = useCallback((text: string, isUser: boolean) => {
    const newMessage: ChatMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text,
      isUser,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const updateTicketForm = useCallback((updates: Partial<TicketForm>) => {
    setTicketForm(prev => ({ ...prev, ...updates }));
  }, []);

  const resetTicketForm = useCallback(() => {
    setTicketForm({
      ...defaultTicketForm,
      email: config.user.email || '',
    });
  }, [config.user.email]);

  const acceptConsent = useCallback(() => {
    setConsentGiven(true);
    if (!isGuest && resolvedApiService?.logConsent) {
      resolvedApiService.logConsent().catch(e =>
        console.warn('[HelpHub] Failed to log consent:', e)
      );
    }
  }, [isGuest, resolvedApiService]);

  const setConfig = useCallback((newConfig: Partial<HelpHubConfig>) => {
    setConfigState(prev => ({ ...prev, ...newConfig }));
  }, []);

  const markChangelogRead = useCallback(() => {
    setUnreadChangelog(0);
    try {
      localStorage.setItem(STORAGE_KEYS.changelogRead, new Date().toISOString());
    } catch (e) {
      console.warn('Failed to save changelog read state:', e);
    }
  }, []);

  // ============================================
  // DERIVED VALUES
  // ============================================

  // Active workspaces (not trial, cancelled, etc.)
const activeWorkspaces = workspaces.filter(ws =>
    ws.subscription_status === 'active' ||
    ws.subscription_status === 'lifetime'
  );

  const activeWorkspaceCount = activeWorkspaces.length;

  // Workspaces that count toward the TIER TAG only (support triage / analytics).
  // Includes in-progress trials ('trial' and 'transfer_trial') so a trialing
  // prospect tags as their intended tier (e.g. 'scale') instead of 'free'.
  // Deliberately separate from activeWorkspaces so trials do NOT affect
  // live-chat access. Excludes 'trial_expired' and 'cancelled'.
  const TRIAL_TAG_STATUSES = new Set(['trial', 'transfer_trial']);
  const tierWorkspaces = workspaces.filter(ws =>
    ws.subscription_status === 'active' ||
    ws.subscription_status === 'lifetime' ||
    TRIAL_TAG_STATUSES.has(ws.subscription_status)
  );

  // Check for live chat feature (feature code 117)
  const hasLiveChatFeature = activeWorkspaces.some(ws =>
    ws.base_license_features?.['117'] === true
  );

  // Human chat access: 2+ workspaces OR has feature 117
  const hasHumanChatAccess = !isGuest && (activeWorkspaceCount >= 2 || hasLiveChatFeature);

// Determine user tier for display/analytics.
  // Prefer tier_code (canonical lowercase, e.g. 'scale', 'lifetime_starter');
  // fall back to subscription_tier (a display name like 'Scale'). Match lower-cased
  // so 'Starter'/'Lifetime Starter' don't silently miss.
  const tierStr = (ws: any) => String(ws.tier_code || ws.subscription_tier || '').toLowerCase();
  const userTier = (() => {
    if (isGuest) return 'free';
const tiers = tierWorkspaces.map(tierStr);
    if (tiers.some(t => t.includes('partner'))) return 'partner';
    if (tiers.some(t => t.includes('enterprise'))) return 'enterprise';
    if (tiers.some(t => t.includes('scale'))) return 'scale';
    if (tiers.some(t => t.includes('growth'))) return 'growth';
    if (tiers.some(t => t.includes('starter'))) return 'starter';
    return 'free';
  })();

  // Update features based on derived values
  useEffect(() => {
    setConfigState(prev => ({
      ...prev,
      features: {
        ...prev.features,
        liveChat: hasHumanChatAccess,
        // Hide "My Tickets" for guests
        tickets: !isGuest,
      },
    }));
  }, [hasHumanChatAccess, isGuest]);

  const value: HelpHubContextType = {
    isOpen,
    currentView,
    messages,
    ticketForm,
    consentGiven,
    config,
    isTyping,
    workspaces,
    workspacesLoading,
    systemStatus,
    frillToken,
    userTickets,
    ticketsLoading,
    unreadChangelog,
    pendingChatMessage,
    open,
    close,
    toggle,
    setView,
    goHome,
    addMessage,
    clearMessages,
    updateTicketForm,
    resetTicketForm,
    acceptConsent,
    setConfig,
    setIsTyping,
    setPendingChatMessage,
    refreshSystemStatus,
    refreshFrillToken,
    refreshUserTickets,
    markChangelogRead,
    hasHumanChatAccess,
    hasLiveChatFeature,
    activeWorkspaceCount,
    isGuest,
    userTier,
    apiService: resolvedApiService,
  };

  return (
    <HelpHubContext.Provider value={value}>
      {children}
    </HelpHubContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================
export function useHelpHub(): HelpHubContextType {
  const context = useContext(HelpHubContext);
  if (!context) {
    throw new Error('useHelpHub must be used within a HelpHubProvider');
  }
  return context;
}

export default HelpHubContext;
